<script setup lang="ts">
import { computed, type Ref, ref, watch } from 'vue'

import { Selector } from '..'
import { type Segment, type SegmentSelectorFilter } from '../../types'
import { debounce } from '../../utils'

type UseLoadSegmentsReturn = {
  data: Readonly<Ref<readonly Segment[]>>
  isLoading: Readonly<Ref<boolean>>
  error: Ref<Error | null>
}

type SegmentOption = { id: number; label: string; segment: Segment }

const props = withDefaults(
  defineProps<{
    advertiserGroupId?: number
    advertiserId?: number
    placeholder?: string
    label?: string
    shouldTruncateText?: boolean
    width?: string | number
    size?: 'default' | 'large'
    isDisabled?: boolean
    loadSegments: (filterRef: Ref<SegmentSelectorFilter>) => UseLoadSegmentsReturn
  }>(),
  {
    placeholder: 'Search segments',
    label: 'Segment',
    width: 240,
    size: 'default',
    isDisabled: false,
  },
)

const segment = defineModel<Pick<Segment, 'id' | 'name'> | undefined>('segment')

const buildFilter = (advertiserGroupId?: number, advertiserId?: number): SegmentSelectorFilter => ({
  query: '',
  advertiserGroup: advertiserGroupId ? { id: advertiserGroupId, name: '' } : undefined,
  advertiser: advertiserId ? { id: advertiserId, label: '' } : undefined,
})

const filter = ref<SegmentSelectorFilter>(buildFilter(props.advertiserGroupId, props.advertiserId))
const { data, isLoading } = props.loadSegments(filter)

// Selector needs numeric ids; each segment id keeps a stable key so selection survives reloads.
const keysBySegmentId = new Map<string, number>()
const keyOf = (segmentId: string) => {
  const existing = keysBySegmentId.get(segmentId)
  if (existing !== undefined) {
    return existing
  }
  const key = keysBySegmentId.size + 1
  keysBySegmentId.set(segmentId, key)
  return key
}

const segmentOptions = computed<SegmentOption[]>(() => data.value.map((item) => ({ id: keyOf(item.id), label: item.name, segment: item })))
const selectedOption = computed(() => (segment.value ? { id: keyOf(segment.value.id), label: segment.value.name } : null))
const selectedLabel = computed(() => segment.value?.name ?? props.label)

const handleSearchQuery = debounce((value: string) => (filter.value.query = value), 500)
const handleSegmentSelection = (option: { id: number } | null) => {
  segment.value = option ? segmentOptions.value.find((item) => item.id === option.id)?.segment : undefined
}
const handleVisible = () => (filter.value.query = '')

watch(
  () => [props.advertiserGroupId, props.advertiserId] as const,
  ([advertiserGroupId, advertiserId]) => {
    filter.value = buildFilter(advertiserGroupId, advertiserId)
    if (segment.value) {
      segment.value = undefined
    }
  },
)
</script>
<template>
  <Selector
    :options="segmentOptions"
    :selected-option="selectedOption"
    :selected-label="selectedLabel"
    :show-search="true"
    :loading="isLoading"
    :placeholder="placeholder"
    :should-truncate-text="shouldTruncateText"
    show-clear-button
    :query="filter.query"
    :width="width"
    :size="size"
    :is-disabled="isDisabled"
    @search="handleSearchQuery"
    @update:selected-option="handleSegmentSelection"
    @visible-change="handleVisible"
  />
</template>
